/**
 * @fileoverview Set a class's schedule: its term of meetings.
 *
 * **Set once, then shown as set.** Until a class has meetings this is the
 * form. Once its term is in place it collapses to a check, the pattern and
 * the dates, with Change to open it again — a section that stayed a blank
 * form gave no sign the job was done. Change opens on the term in place (the
 * class read's `term`), never a blank form, so what is there is what gets
 * adjusted.
 *
 * **A change says how far it reaches.** Once a class has met, a change goes
 * to the remaining classes (today on: every earlier one stays exactly as it
 * is, with its lesson notes and attendance) or to all of them, which can
 * overwrite the past and says so before anything is previewed. Either way a
 * class the new schedule still has keeps everything recorded against it, so
 * adding classes touches none of the existing ones. The preview counts the
 * classes removed, changed and added.
 *
 * **The preview is the feature.** Generating thirty dates and finding out
 * afterwards that four were wrong leaves a schedule nobody can reason about,
 * so every date is shown before the first write and the same generation runs
 * either way — what you approve is what lands.
 *
 * Two ways in, because schedules arrive in two shapes. A term that recurs is
 * described by rules: as many weekday-and-time pairs as the class meets, so
 * "Tuesdays at 4 and Thursdays at 5" is one term rather than two. A term that
 * does not recur cleanly is listed outright, one date per line — the shape a
 * school calendar already has.
 *
 * **The school calendar goes in directly.** A district publishes its year as
 * an .ics file or a column of dates, and retyping twenty holidays out of it is
 * both the tedious part and the part that goes wrong. Either form is read by
 * the same endpoint and lands in whichever box it was dropped into: the days
 * the class meets, or the days it does not.
 *
 * Holidays are excluded up front rather than cancelled one at a time
 * afterwards. A skipped date never becomes a meeting, which is not the same
 * as a meeting that was cancelled: both are absent from the calendar, but only
 * one of them is a fact about the term.
 *
 * A change REPLACES the schedule rather than adding a second term beside it —
 * two overlapping terms would double every meeting.
 *
 * **Its time zone is the server's to name.** A class meets in its program
 * address's time zone, else the organization's (services/schedule_zones); the
 * line under the heading says which and why. Only when neither is set does
 * this browser's zone stand in, and the line says to set the organization's.
 *
 * **Night times are named.** A class almost never meets between midnight and
 * 6 AM, and a time there is nearly always an afternoon one left on AM (the
 * time field starts on AM). Each one is called out where it was entered, with
 * its PM twin one click away, and the preview counts any that remain. Times
 * read as 12-hour clock times throughout, so "1:50 AM" is never "01:50".
 *
 * The day and time of a meeting start blank: a prefilled "Tue, 4:00 PM" read
 * as a schedule already chosen. A class made from a template opens prefilled
 * (`initial`, the class's `term_draft`): the template's meeting days and times
 * and its holidays, ready to adjust and preview. Nothing is scheduled until
 * the preview is applied.
 */

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { formatTime12h, MONTHS_SHORT, WEEKDAYS, WEEKDAYS_SHORT as DAYS } from "@/lib/schedule";
import { Card, SectionHeader, Button, Tag, Icon, SegmentedControl } from "@/components/studio/primitives";

const inputClass =
  "rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** Where a class's time zone comes from, said beside it (services/schedule_zones). */
const ZONE_SOURCES = { program: "the program's address", organization: "your organization's time zone" };

/** A meeting time between midnight and 6 AM ("HH:MM"). */
export const isNightTime = (time) => /^0[0-5]:[0-5]\d$/.test(time || "");

/** The same clock time twelve hours on: "01:50" → "13:50", "00:30" → "12:30". */
export const pmTwin = (time) => `${Number(time.slice(0, 2)) + 12}${time.slice(2)}`;

/** The pasted list with every night time in it moved to its PM twin. */
export function pastedAtPm(text) {
  return text
    .split("\n")
    .map((line) => {
      const parts = line.split(",");
      const time = (parts[1] || "").trim();
      if (!isNightTime(time)) return line;
      parts[1] = parts[1].replace(time, pmTwin(time));
      return parts.join(",");
    })
    .join("\n");
}

/** "1:50 AM is in the middle of the night." with a one-click PM fix. */
function NightTime({ time, onFix, testId }) {
  if (!isNightTime(time)) return null;
  return (
    <p
      className="flex flex-wrap items-center gap-x-2 gap-y-1 font-sans text-[13px] text-[var(--state-warning)]"
      data-testid={testId}
    >
      <Icon name="alert" size={14} className="shrink-0" />
      <span>{formatTime12h(time)} is in the middle of the night.</span>
      <button
        type="button"
        onClick={onFix}
        data-testid={`${testId}-fix`}
        className="font-medium text-[var(--accent-yellow-text)] hover:underline"
      >
        Use {formatTime12h(pmTwin(time))}
      </button>
    </p>
  );
}

/* A new meeting day has no day and no time until someone picks them. */
const emptyRule = () => ({ weekday: "", start_time_local: "", duration_minutes: 60 });

const splitDates = (raw) => raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

/** "Tuesdays and Thursdays at 4:00 PM", or each day at its own time. */
export function describeRules(rules = []) {
  const sorted = [...rules].sort(
    (a, b) => a.weekday - b.weekday || String(a.start_time_local).localeCompare(String(b.start_time_local)),
  );
  if (sorted.length === 0) return "";
  if (new Set(sorted.map((r) => r.start_time_local)).size === 1) {
    const days = sorted.map((r) => `${WEEKDAYS[r.weekday]}s`);
    const list = days.length > 1 ? `${days.slice(0, -1).join(", ")} and ${days[days.length - 1]}` : days[0];
    return `${list} at ${formatTime12h(sorted[0].start_time_local)}`;
  }
  return sorted.map((r) => `${DAYS[r.weekday]} ${formatTime12h(r.start_time_local)}`).join(", ");
}

/** "Sep 8 – Dec 15, 2026", with the year once when the term sits in one. */
function termRange(first, last) {
  const [y1, m1, d1] = String(first).split("-").map(Number);
  const [y2, m2, d2] = String(last).split("-").map(Number);
  return `${MONTHS_SHORT[m1 - 1]} ${d1}${y1 === y2 ? "" : `, ${y1}`} – ${MONTHS_SHORT[m2 - 1]} ${d2}, ${y2}`;
}

/** One line for a term in place: its pattern, its dates and how many classes. */
export function termSummary(term, scheduled) {
  const count = `${scheduled} class${scheduled === 1 ? "" : "es"}`;
  if (term?.mode === "dates") {
    const days = (term.dates || []).map((d) => d.date).sort();
    return days.length ? `${termRange(days[0], days[days.length - 1])} · ${count}` : count;
  }
  return [describeRules(term?.rules), term?.starts_on && termRange(term.starts_on, term.ends_on), count]
    .filter(Boolean)
    .join(" · ");
}

/**
 * What a preview changes about the term the class already has. A class with
 * no term yet has nothing to change: the server reports every meeting as
 * "added", which is the date list below said again, under a heading about a
 * current term that does not exist. So a first term lists no changes.
 */
export function termChanges(preview) {
  const changes = preview?.change_details || [];
  const firstTerm =
    changes.length > 0 &&
    changes.every((c) => c.kind === "added" && !c.before) &&
    changes.length === (preview.occurrences || []).length;
  return firstTerm ? [] : changes;
}

/**
 * How many classes a preview removes, changes and adds, and how many of those
 * changes reach a class that already happened (`past`). Read from the
 * server's own count; an older server's lists of ids say the same, without
 * the past.
 */
export function countChanges(preview) {
  if (preview?.change_counts) return preview.change_counts;
  const ids = preview?.changes || {};
  return { removed: ids.removed?.length || 0, changed: ids.changed?.length || 0, added: ids.added?.length || 0, past: 0 };
}

/** A template's draft as the pasted-dates box writes it: one meeting a line. */
const draftLines = (dates = []) =>
  dates
    .map((d) => [d.date, d.start_time_local, d.duration_minutes].filter(Boolean).join(", "))
    .join("\n");

/**
 * The class's schedule: the form until it has one, then the term in place,
 * collapsed, with Change to open the form on it again.
 *
 * `current` is the term in place (the class read's `term`, null before the
 * first one), `scheduled` how many of its classes still meet, and `hasPast`
 * whether any has already happened — only then is there a past for a change
 * to reach, and so a choice to make about it.
 */
export default function ClassScheduleBuilder({ current = null, scheduled = 0, ...props }) {
  const [open, setOpen] = useState(!current);
  if (current && !open) {
    return <ScheduleSet term={current} scheduled={scheduled} onChange={() => setOpen(true)} />;
  }
  // Mounted afresh on every Change, so Cancel throws its edits away with it.
  return <TermForm {...props} current={current} onCancel={current ? () => setOpen(false) : null} />;
}

/** The term in place, collapsed: a check, what it is, and the way back in. */
function ScheduleSet({ term, scheduled, onChange }) {
  return (
    <Card className="mb-6" data-testid="schedule-set">
      <div className="flex items-center gap-3">
        <span
          role="img"
          aria-label="Schedule set"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--state-success-border)] bg-[var(--state-success-dim)]"
        >
          <Icon name="check" size={16} strokeWidth={2} className="text-[var(--state-success)]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Set schedule</div>
          <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="schedule-summary">
            {termSummary(term, scheduled)}
          </p>
        </div>
        <Button variant="ghost" onPress={onChange} data-testid="schedule-change">
          Change
        </Button>
      </div>
    </Card>
  );
}

function TermForm({ classId, onScheduled, zone = null, initial = null, current, hasPast = false, onCancel }) {
  // The server writes the term in the class's own zone (`zone`); this
  // browser's is sent only for a class whose program and organization have none.
  const timezone = zone?.zone || browserTimezone();
  // Change opens on the term in place; a first term on its template, if any.
  const start = current || initial;
  const [mode, setMode] = useState(start?.mode === "dates" ? "dates" : "recurring");
  const [rules, setRules] = useState(() =>
    start?.rules?.length ? start.rules.map((r) => ({ ...r })) : [emptyRule()],
  );
  const [startsOn, setStartsOn] = useState(start?.starts_on || "");
  const [endsOn, setEndsOn] = useState(start?.ends_on || "");
  const [skipText, setSkipText] = useState((start?.skip_dates || []).join(", "));
  const [pasted, setPasted] = useState(() => draftLines(start?.dates));
  const [defaultTime, setDefaultTime] = useState(start?.default_start_time_local || "16:00");
  const [defaultMinutes, setDefaultMinutes] = useState(start?.default_duration_minutes || 60);
  /* How far a change reaches, once the class has met. The remaining classes
     by default: the past is where lesson notes and attendance already are. */
  const [scope, setScope] = useState("remaining");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(null); // "skips" | "dates"
  const fileInput = useRef(null);
  const importTarget = useRef("skips");

  const skipDates = splitDates(skipText);

  const setRule = (i, key, value) =>
    setRules((rs) => rs.map((r, n) => (n === i ? { ...r, [key]: value } : r)));

  /** Any change invalidates the preview — approving stale dates is the bug
      this whole screen exists to prevent. */
  const invalidate = () => setPreview(null);

  /* A first term is the whole term. A change says how far it reaches: the
     choice above, or the whole term while no class has happened yet. */
  const reach = current ? { scope: hasPast ? scope : "all" } : {};

  const body = (apply) =>
    mode === "recurring"
      ? {
          timezone,
          starts_on: startsOn,
          ends_on: endsOn,
          rules: rules.map((r) => ({ ...r, weekday: Number(r.weekday), duration_minutes: Number(r.duration_minutes) })),
          skip_dates: skipDates,
          ...reach,
          apply,
        }
      : {
          timezone,
          // A list IS its own term, so the server derives the bounds from it
          // rather than taking two dates that could disagree with the list.
          dates: parseDateRows(pasted).rows,
          default_start_time_local: defaultTime,
          default_duration_minutes: Number(defaultMinutes),
          skip_dates: skipDates,
          ...reach,
          apply,
        };

  const run = async (apply) => {
    if (mode === "recurring" && (!startsOn || !endsOn)) {
      toast.error("Give the term a start and an end date");
      return;
    }
    if (mode === "recurring" && rules.some((r) => r.weekday === "" || !r.start_time_local)) {
      toast.error("Choose a day and a start time for each day the class meets");
      return;
    }
    if (mode === "dates") {
      // Reported with its line, never dropped: a term that is quietly one
      // week short is the failure this screen exists to prevent.
      const { rows, problems } = parseDateRows(pasted);
      if (problems.length > 0) {
        toast.error(
          `Line ${problems.slice(0, 3).map((p) => p.line).join(", ")} isn't a date — fix or remove it`,
        );
        return;
      }
      if (rows.length === 0) {
        toast.error("List the dates the class meets, one per line");
        return;
      }
    }
    const payload = { ...body(apply), ...(apply ? {
      expected_revision: preview?.expected_revision, preview_hash: preview?.preview_hash,
    } : {}) };
    setBusy(true);
    try {
      const res = await orgApi.post(`/org/classes/${classId}/schedule`, payload);
      setPreview(res.data?.preview || null);
      if (apply) {
        const n = res.data?.preview?.counts?.meetings ?? 0;
        toast.success(current ? "Schedule updated" : `Schedule set: ${n} class${n === 1 ? "" : "es"}`);
        onScheduled?.();
      }
    } catch (err) {
      setPreview(null);
      toast.error(getOrgErrorMessage(err, "Couldn't work out that schedule"));
    } finally {
      setBusy(false);
    }
  };

  /** Send pasted text or an .ics file's contents to be read into dates. */
  const importCalendar = async (text, target) => {
    if (!text.trim()) return;
    setImporting(target);
    try {
      const res = await orgApi.post("/org/calendar-import", { text });
      const found = res.data?.dates || [];
      if (found.length === 0) {
        toast.error("No dates found in that calendar");
        return;
      }
      if (target === "skips") {
        // Merged, not replaced: a district publishes holidays in more than one
        // file, and the second import must not erase the first.
        const merged = [...new Set([...skipDates, ...found.map((d) => d.date)])].sort();
        setSkipText(merged.join(", "));
      } else {
        setPasted(found.map((d) => d.date).join("\n"));
      }
      invalidate();
      const problems = (res.data?.problems || []).length;
      toast.success(
        `${found.length} date${found.length === 1 ? "" : "s"} read${problems ? ` · ${problems} row${problems === 1 ? "" : "s"} unreadable` : ""}`,
      );
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't read that calendar"));
    } finally {
      setImporting(null);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error("That file is too large to be a calendar");
      return;
    }
    importCalendar(await file.text(), importTarget.current);
  };

  const pickFile = (target) => {
    importTarget.current = target;
    fileInput.current?.click();
  };

  /* Meetings the preview has between midnight and 6 AM, still meeting. */
  const nightMeetings = preview
    ? (preview.occurrences || preview.meetings || []).filter(
        (m) => isNightTime(m.start_time_local) && m.status !== "cancelled",
      ).length
    : 0;
  const pastedNight = mode === "dates" && parseDateRows(pasted).rows.some((r) => isNightTime(r.start_time_local));

  /* What the preview does to the term in place: nothing to count for a first term. */
  const counted = current && preview ? countChanges(preview) : null;
  const nothingChanges = counted && counted.removed + counted.changed + counted.added === 0;
  const applyLabel = !current
    ? "Save schedule"
    : !hasPast
      ? "Apply changes"
      : scope === "remaining"
        ? "Apply to remaining classes"
        : "Apply to all classes";

  return (
    <Card className="mb-6" data-testid="schedule-builder">
      <SectionHeader>Set schedule</SectionHeader>
      <p className="mb-3 text-sm text-[var(--text-secondary)]" data-testid="schedule-zone">
        Meeting times: {timezone.replaceAll("_", " ")}
        <span className="text-[var(--text-tertiary)]">
          {" "}
          ({zone?.source ? ZONE_SOURCES[zone.source] || zone.source : "this computer's time zone"})
        </span>
      </p>
      {!zone?.source && (
        <p className="-mt-2 mb-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="schedule-zone-unset">
          Your organization has no time zone yet, so this computer&rsquo;s is used.{" "}
          <Link to="/org/settings/schedule" className="font-medium text-[var(--accent-yellow-text)] hover:underline">
            Set it in Settings → Schedule
          </Link>{" "}
          so every class uses one.
        </p>
      )}
      {initial && !current && (
        <p className="mb-3 flex items-start gap-2 text-sm text-[var(--text-secondary)]" data-testid="schedule-from-template">
          <Icon name="copy" size={15} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
          <span>
            Filled in from {initial.from_class_name ? `“${initial.from_class_name}”` : "the template"}. Check the dates for
            this term, then preview: nothing is scheduled until you apply it.
          </span>
        </p>
      )}
      <div className="mb-4">
        <SegmentedControl
          testId="schedule-mode"
          value={mode}
          onChange={(v) => { setMode(v); invalidate(); }}
          options={[
            { id: "recurring", label: "Repeats weekly" },
            { id: "dates", label: "List the dates" },
          ]}
        />
      </div>

      {/* One hidden picker for both boxes; `importTarget` says which asked. */}
      <input
        ref={fileInput}
        type="file"
        accept=".ics,.csv,.txt,text/calendar,text/csv,text/plain"
        onChange={onFile}
        data-testid="schedule-file"
        className="hidden"
      />

      {mode === "recurring" ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block font-sans text-[12px] text-[var(--text-tertiary)]">Term starts</span>
              <input type="date" value={startsOn} onChange={(e) => { setStartsOn(e.target.value); invalidate(); }}
                     aria-label="Term start" data-testid="schedule-start" className={`${inputClass} w-full`} />
            </label>
            <label className="block">
              <span className="mb-1 block font-sans text-[12px] text-[var(--text-tertiary)]">Term ends</span>
              <input type="date" value={endsOn} onChange={(e) => { setEndsOn(e.target.value); invalidate(); }}
                     aria-label="Term end" data-testid="schedule-end" className={`${inputClass} w-full`} />
            </label>
          </div>

          <div className="mt-4">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
              When it meets
            </div>
            <div className="flex flex-col gap-2">
              {rules.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`schedule-rule-${i}`}>
                  <select value={r.weekday} onChange={(e) => { setRule(i, "weekday", e.target.value); invalidate(); }}
                          aria-label={`Day ${i + 1}`} data-testid={`schedule-rule-day-${i}`} className={inputClass}>
                    <option value="" disabled>Choose a day</option>
                    {DAYS.map((d, n) => <option key={d} value={n}>{d}</option>)}
                  </select>
                  <input type="time" value={r.start_time_local}
                         onChange={(e) => { setRule(i, "start_time_local", e.target.value); invalidate(); }}
                         aria-label={`Time ${i + 1}`} data-testid={`schedule-rule-time-${i}`} className={inputClass} />
                  <input type="number" min={5} max={600} step={5} value={r.duration_minutes}
                         onChange={(e) => { setRule(i, "duration_minutes", e.target.value); invalidate(); }}
                         aria-label={`Minutes ${i + 1}`} data-testid={`schedule-rule-mins-${i}`}
                         className={`${inputClass} w-24`} />
                  <span className="font-sans text-[13px] text-[var(--text-tertiary)]">min</span>
                  {rules.length > 1 && (
                    <button type="button" onClick={() => { setRules((rs) => rs.filter((_, n) => n !== i)); invalidate(); }}
                            aria-label={`Remove day ${i + 1}`} data-testid={`schedule-rule-remove-${i}`}
                            className="font-sans text-[13px] text-[var(--state-danger)] hover:underline">
                      Remove
                    </button>
                  )}
                  <div className="basis-full">
                    <NightTime
                      time={r.start_time_local}
                      onFix={() => { setRule(i, "start_time_local", pmTwin(r.start_time_local)); invalidate(); }}
                      testId={`schedule-rule-night-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => { setRules((rs) => [...rs, emptyRule()]); invalidate(); }}
                    data-testid="schedule-add-rule"
                    className="mt-2 inline-flex items-center gap-1 font-sans text-[14px] font-medium text-[var(--accent-yellow-text)] hover:underline">
              <Icon name="plus" size={15} /> Add another day
            </button>
          </div>
        </>
      ) : (
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
              One date per line
            </span>
            <button type="button" onClick={() => pickFile("dates")} data-testid="schedule-import-dates"
                    className="inline-flex items-center gap-1 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] hover:underline">
              <Icon name="plus" size={14} /> {importing === "dates" ? "Reading…" : "Import a calendar file"}
            </button>
          </div>
          <textarea value={pasted} onChange={(e) => { setPasted(e.target.value); invalidate(); }} rows={6}
                    placeholder={"2026-09-08\n2026-09-15, 17:00\n2026-09-22, 17:00, 45"}
                    aria-label="Dates the class meets" data-testid="schedule-paste" className={`${inputClass} w-full`} />
          <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
            Date, then optionally a time (24-hour, so 4 PM is 16:00) and a length. A row that isn&rsquo;t a date
            is reported rather than skipped, so a typo doesn&rsquo;t quietly shorten the term.
          </p>
          {pastedNight && (
            <p
              className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-sans text-[13px] text-[var(--state-warning)]"
              data-testid="schedule-paste-night"
            >
              <Icon name="alert" size={14} className="shrink-0" />
              <span>Some of these times are between midnight and 6 AM.</span>
              <button
                type="button"
                onClick={() => { setPasted(pastedAtPm(pasted)); invalidate(); }}
                data-testid="schedule-paste-night-fix"
                className="font-medium text-[var(--accent-yellow-text)] hover:underline"
              >
                Move them to PM
              </button>
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2">
              <span className="font-sans text-[12px] text-[var(--text-tertiary)]">Usually at</span>
              <input type="time" value={defaultTime} onChange={(e) => { setDefaultTime(e.target.value); invalidate(); }}
                     aria-label="Default time" data-testid="schedule-default-time" className={inputClass} />
            </label>
            <label className="flex items-center gap-2">
              <input type="number" min={5} max={600} step={5} value={defaultMinutes}
                     onChange={(e) => { setDefaultMinutes(e.target.value); invalidate(); }}
                     aria-label="Default minutes" data-testid="schedule-default-mins"
                     className={`${inputClass} w-24`} />
              <span className="font-sans text-[12px] text-[var(--text-tertiary)]">min, unless a row says otherwise</span>
            </label>
          </div>
          <div className="mt-2">
            <NightTime
              time={defaultTime}
              onFix={() => { setDefaultTime(pmTwin(defaultTime)); invalidate(); }}
              testId="schedule-default-night"
            />
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
            Holidays and no-class days
          </span>
          <button type="button" onClick={() => pickFile("skips")} data-testid="schedule-import-skips"
                  className="inline-flex items-center gap-1 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] hover:underline">
            <Icon name="plus" size={14} /> {importing === "skips" ? "Reading…" : "Import the school calendar"}
          </button>
        </div>
        <textarea value={skipText} onChange={(e) => { setSkipText(e.target.value); invalidate(); }} rows={2}
                  placeholder="2026-11-26, 2026-12-24 — dates the class won't meet"
                  aria-label="Skip dates" data-testid="schedule-skips" className={`${inputClass} w-full`} />
        <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
          Paste the district&rsquo;s closure dates, or import its calendar file — the days it names are added to
          the ones already here.
        </p>
      </div>

      {/* How far the change reaches sits with the buttons that make it, so
          the warning is in view at the moment of previewing. */}
      {current && hasPast && (
        <div className="mt-5 border-t border-[var(--studio-divider)] pt-4" data-testid="schedule-scope-block">
          <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Apply changes to</div>
          <SegmentedControl
            testId="schedule-scope"
            value={scope}
            onChange={(v) => { setScope(v); invalidate(); }}
            options={[
              { id: "remaining", label: "Remaining classes", testId: "schedule-scope-remaining" },
              { id: "all", label: "All classes", testId: "schedule-scope-all" },
            ]}
          />
          {scope === "remaining" ? (
            <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="schedule-scope-note">
              Future classes only. Classes that have already happened stay exactly as they are, with their lesson
              notes and attendance.
            </p>
          ) : (
            <p
              className="mt-2 flex items-start gap-2 rounded-[12px] border border-[var(--state-warning-border)] bg-[var(--state-warning-dim)] px-3 py-2 font-sans text-[13px] text-[var(--state-warning)]"
              data-testid="schedule-scope-warning"
            >
              <Icon name="alert" size={15} className="mt-0.5 shrink-0" />
              <span>
                Past and future classes. This can overwrite past classes: lesson notes and attendance already
                recorded for a past class that moves or is removed will no longer be linked to it.
              </span>
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" onPress={() => run(false)} disabled={busy} data-testid="schedule-preview">
          {busy ? "Working…" : "Preview dates"}
        </Button>
        {/* Applying a change that changes nothing would only tell every
            instructor their schedule was updated when it was not. */}
        {preview && !nothingChanges && (
          <Button onPress={() => run(true)} disabled={busy} data-testid="schedule-apply">
            {applyLabel}
          </Button>
        )}
        {onCancel && (
          <Button variant="bare" onPress={onCancel} disabled={busy} data-testid="schedule-cancel">
            Cancel
          </Button>
        )}
      </div>

      {preview && (
        <div className="mt-4" data-testid="schedule-preview-result">
          {counted ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="schedule-change-counts">
              {nothingChanges ? (
                <span className="font-sans text-[14px] text-[var(--text-secondary)]">
                  No classes change: this is the schedule already in place.
                </span>
              ) : (
                <>
                  <Tag>{counted.removed} removed</Tag>
                  <Tag>{counted.changed} changed</Tag>
                  <Tag>{counted.added} added</Tag>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Tag accent>{preview.counts.meetings} classes</Tag>
              {preview.counts.skipped > 0 && <Tag>{preview.counts.skipped} skipped</Tag>}
              <span className="font-sans text-[13px] text-[var(--text-tertiary)]">
                over {preview.counts.weeks} week{preview.counts.weeks === 1 ? "" : "s"}
              </span>
            </div>
          )}
          {nightMeetings > 0 && (
            <p data-testid="schedule-night-meetings" className="mt-2 font-sans text-[13px] text-[var(--state-warning)]">
              {nightMeetings === 1 ? "1 class starts" : `${nightMeetings} classes start`} between midnight and 6 AM.
              Check that they shouldn&rsquo;t be PM before you apply.
            </p>
          )}
          {counted?.past > 0 && (
            <p data-testid="schedule-past-changes" className="mt-2 font-sans text-[13px] text-[var(--state-warning)]">
              {counted.past === 1 ? "1 of these changes is to a class" : `${counted.past} of these changes are to classes`}{" "}
              that already happened.
            </p>
          )}

          {termChanges(preview).length > 0 && <div className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="schedule-term-changes">
            <p className="font-medium">Changes to the current term</p>
            <ul className="max-h-64 overflow-y-auto">{termChanges(preview).map((change, index) => <li key={index}>
              {change.kind === "added" ? "Add" : change.kind === "removed" ? "Remove" : "Change"}{" "}
              {change.before && `${change.before.date} ${formatTime12h(change.before.start_time_local)} (${change.before.status})`}
              {change.before && change.after && " → "}
              {change.after && `${change.after.date} ${formatTime12h(change.after.start_time_local)} (${change.after.status})`}
            </li>)}</ul>
          </div>}
          {preview.unused_skip_dates?.length > 0 && (
            // Almost always the wrong day in the holiday list.
            <p data-testid="schedule-unused-skips" className="mt-2 font-sans text-[13px] text-[var(--state-warning)]">
              {preview.unused_skip_dates.join(", ")} — the class doesn&rsquo;t meet on{" "}
              {preview.unused_skip_dates.length === 1 ? "that day" : "those days"} anyway.
            </p>
          )}

          <div className="mt-3 max-h-64 overflow-y-auto rounded-[12px] border border-[var(--studio-divider)]">
            {(preview.occurrences || preview.meetings).map((m) => (
              <div key={m.occurrence_id || `${m.date}-${m.start_time_local}`}
                   className="flex items-center justify-between gap-3 border-b border-[var(--studio-divider)] px-3 py-1.5 last:border-b-0">
                <span className="font-mono text-[13px] text-[var(--text-primary)]">{m.date}</span>
                <span className="font-sans text-[13px] text-[var(--text-tertiary)]">
                  {formatTime12h(m.start_time_local)} · {m.duration_minutes} min
                  {m.status === "cancelled" && " · Cancelled"}
                </span>
              </div>
            ))}
            {preview.skipped.filter(m => !preview.occurrences?.some(o => o.date === m.date && o.start_time_local === m.start_time_local)).map((m) => (
              <div key={`skip-${m.date}-${m.start_time_local}`}
                   className="flex items-center justify-between gap-3 border-b border-[var(--studio-divider)] px-3 py-1.5 last:border-b-0 opacity-60">
                <span className="font-mono text-[13px] text-[var(--text-primary)] line-through">{m.date}</span>
                <Tag>No class</Tag>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Read the pasted list into `{rows, problems}`.
 *
 * A row that is not a date is REPORTED with its line number rather than
 * dropped. Dropping it would send a shorter term than the one on screen, and
 * the whole point of previewing is that what you approved is what lands.
 */
export function parseDateRows(text) {
  const rows = [];
  const problems = [];
  text.split("\n").forEach((raw, i) => {
    const line = raw.trim().replace(/,+$/, "");
    if (!line || line.startsWith("#")) return;
    const [date, time, mins] = line.split(",").map((p) => p.trim());
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      problems.push({ line: i + 1, value: line });
      return;
    }
    const row = { date };
    if (/^([01]\d|2[0-3]):[0-5]\d$/.test(time || "")) row.start_time_local = time;
    if (mins && Number(mins) > 0) row.duration_minutes = Number(mins);
    rows.push(row);
  });
  return { rows, problems };
}
